/**
 * useGoToReport — navigates back to the Report page for a business, via the app router
 * (never `history.back()`, so it also works when the Builder was opened directly, e.g. a
 * bookmarked/shared `/builder/:id` URL with no browser history to go back to).
 *
 * The Report route is `/report/:scanId` — a DIFFERENT id than the business id the Builder
 * itself is keyed by (`BuilderRecord.businessId`), so the business id alone can't build a
 * valid Report URL. Prefers the `scanId` the caller arrived with (passed as router state
 * when opening the Builder from a Report page — see `ImproveWebsiteCTA`), which returns to
 * the EXACT report the user came from. Falls back to the business's most recently created
 * scan (via `scanApi.list`) when no scanId is available, and to `/` when the business has
 * no scans at all or the lookup fails.
 */
import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useI18nNavigate } from './useI18nNavigate'
import { scanApi } from '../api/client'

export function useGoToReport(businessId: string | undefined) {
  const navigate = useI18nNavigate()
  const location = useLocation()

  return useCallback(async () => {
    const stateScanId = (location.state as { scanId?: string } | null)?.scanId
    if (stateScanId) {
      navigate(`/report/${stateScanId}`)
      return
    }
    if (!businessId) {
      navigate('/')
      return
    }
    try {
      const scans = await scanApi.list(businessId)
      const latest = [...scans].sort((a, b) => b.created_at.localeCompare(a.created_at))[0]
      navigate(latest ? `/report/${latest.id}` : '/')
    } catch {
      navigate('/')
    }
  }, [navigate, location.state, businessId])
}
